/**
 * IQ-Service-Inventory React-Query Hooks (Spec 344).
 *
 * Cursor-paginierte Liste (useInfiniteQuery — "Mehr laden"), Detail, und die
 * Create/Update/Archive-Mutationen. Query-Keys sind auf `merchantId` gescoped.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  archiveIqServiceInventoryItem,
  createIqServiceInventoryItem,
  getIqServiceInventoryItem,
  getPlatformIqServiceInventory,
  listIqServiceInventory,
  updateIqServiceInventoryItem,
} from '@/lib/api/iq-service-inventory';
import type {
  CreateIqServiceInventoryInput,
  IqServiceInventoryFilters,
  UpdateIqServiceInventoryInput,
} from '@/types/iq-service-inventory';

export const iqServiceInventoryKeys = {
  all: ['iq-service-inventory'] as const,
  lists: (merchantId: string) => [...iqServiceInventoryKeys.all, merchantId, 'list'] as const,
  list: (merchantId: string, filters?: IqServiceInventoryFilters) =>
    [...iqServiceInventoryKeys.lists(merchantId), filters ?? {}] as const,
  details: (merchantId: string) => [...iqServiceInventoryKeys.all, merchantId, 'detail'] as const,
  detail: (merchantId: string, iqServiceKey: string) =>
    [...iqServiceInventoryKeys.details(merchantId), iqServiceKey] as const,
  // Spec 347 §8: Cross-Merchant-Aggregat. BEWUSST kein merchantId im Key — getrennt
  // von der per-Merchant-Key-Familie, damit die 5-min-Reporting-Uhr und die
  // 30-s-Per-Merchant-Uhr nie denselben Cache-Eintrag teilen.
  platformOverview: () => [...iqServiceInventoryKeys.all, 'platform-overview'] as const,
};

/**
 * Paginated inventory list. Pages accumulate via `fetchNextPage`; `hasNextPage`
 * reflects a non-null `nextCursor`. Flatten with `data.pages.flatMap(p => p.items)`.
 */
export function useIqServiceInventory(
  merchantId: string | undefined,
  filters?: IqServiceInventoryFilters,
) {
  return useInfiniteQuery({
    queryKey: iqServiceInventoryKeys.list(merchantId ?? '', filters),
    queryFn: ({ pageParam }) =>
      listIqServiceInventory(merchantId!, filters, pageParam ?? undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!merchantId,
    staleTime: 30_000,
  });
}

/**
 * Cross-merchant inventory aggregate (Spec 347 §8), platform-only. Reporting view:
 * own query key (`['iq-service-inventory', 'platform-overview']`) + 5-min staleTime,
 * deliberately separate from the 30-s per-merchant clock so the two never share a
 * cache entry. The route gate (IQ_SERVICE_INVENTORY_OVERVIEW) keeps it platform-only.
 */
export function usePlatformIqServiceInventory() {
  return useQuery({
    queryKey: iqServiceInventoryKeys.platformOverview(),
    queryFn: () => getPlatformIqServiceInventory(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useIqServiceInventoryItem(
  merchantId: string | undefined,
  iqServiceKey: string | undefined,
) {
  return useQuery({
    queryKey: iqServiceInventoryKeys.detail(merchantId ?? '', iqServiceKey ?? ''),
    queryFn: () => getIqServiceInventoryItem(merchantId!, iqServiceKey!),
    enabled: !!merchantId && !!iqServiceKey,
    staleTime: 30_000,
  });
}

export function useCreateIqServiceInventoryItem(merchantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateIqServiceInventoryInput) =>
      createIqServiceInventoryItem(merchantId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: iqServiceInventoryKeys.lists(merchantId) });
      // Spec 356 (Weg A): the cross-merchant overview aggregate must also refresh after
      // any inventory write. On the per-merchant page this is a no-op (inactive observer
      // → marked stale, no network call); on the overview it refetches immediately.
      queryClient.invalidateQueries({ queryKey: iqServiceInventoryKeys.platformOverview() });
    },
  });
}

export function useUpdateIqServiceInventoryItem(merchantId: string, iqServiceKey: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateIqServiceInventoryInput) =>
      updateIqServiceInventoryItem(merchantId, iqServiceKey, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: iqServiceInventoryKeys.lists(merchantId) });
      queryClient.invalidateQueries({
        queryKey: iqServiceInventoryKeys.detail(merchantId, iqServiceKey),
      });
      // Spec 356 (Weg A): also refresh the cross-merchant overview aggregate.
      queryClient.invalidateQueries({ queryKey: iqServiceInventoryKeys.platformOverview() });
    },
  });
}

export function useArchiveIqServiceInventoryItem(merchantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (iqServiceKey: string) => archiveIqServiceInventoryItem(merchantId, iqServiceKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: iqServiceInventoryKeys.lists(merchantId) });
      // Spec 356 (Weg A): also refresh the cross-merchant overview aggregate.
      queryClient.invalidateQueries({ queryKey: iqServiceInventoryKeys.platformOverview() });
    },
  });
}
